import React, { useState } from "react";
import axios from "axios";
import { Send, Loader2, Mail, Phone, MessageCircle, MapPin } from "lucide-react";
import { toast } from "sonner";
import { CONTACT } from "../data/site";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BUSINESS_TYPES = [
  "Importer",
  "Wholesale Distributor",
  "Supermarket Chain",
  "Retail Chain",
  "Cash & Carry",
  "Indian Grocery Store",
  "Food Service Company",
  "Ethnic Food Importer",
  "Other",
];

const initial = {
  company_name: "",
  contact_person: "",
  country: "",
  business_type: "",
  email: "",
  phone: "",
  whatsapp: "",
  products_required: "",
  estimated_quantity: "",
  destination_port: "",
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/inquiries`, form);
      toast.success("Inquiry submitted", {
        description: res.data?.message || "Our export team will contact you shortly.",
      });
      setForm(initial);
    } catch (err) {
      const detail =
        err.response?.data?.detail?.[0]?.msg ||
        err.response?.data?.detail ||
        "Please check your details and try again.";
      toast.error("Submission failed", { description: String(detail) });
    } finally {
      setSubmitting(false);
    }
  };

  const waHref = `https://wa.me/${CONTACT.whatsappRaw}?text=${encodeURIComponent(
    "Hello Babul Corporation, I'd like to enquire about Indian FMCG exports."
  )}`;
  const mailHref = `mailto:${CONTACT.email}?subject=${encodeURIComponent(
    "FMCG Export Inquiry"
  )}`;

  return (
    <section id="contact" data-testid="contact-section" className="py-20 lg:py-32 bg-white relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10">
          {/* Left panel */}
          <div className="lg:col-span-5">
            <div className="overline mb-4">Get in Touch</div>
            <h2 className="section-heading text-navy text-3xl md:text-4xl lg:text-5xl font-bold">
              Start your Indian FMCG sourcing journey today.
            </h2>
            <p className="mt-5 text-lg text-slate-600">
              Share your product list and destination — we'll respond with a
              transparent, competitive quotation within one business day.
            </p>

            <div className="mt-8 space-y-4">
              <a
                href={waHref}
                target="_blank"
                rel="noreferrer"
                data-testid="contact-whatsapp-btn"
                className="flex items-center gap-4 p-5 rounded-2xl bg-emerald text-white hover:bg-[#004a35] transition-colors"
              >
                <div className="w-11 h-11 rounded-xl bg-white/15 flex items-center justify-center">
                  <MessageCircle className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest opacity-80">WhatsApp Us</div>
                  <div className="font-semibold">{CONTACT.whatsapp}</div>
                </div>
              </a>

              <a
                href={mailHref}
                data-testid="contact-email-btn"
                className="flex items-center gap-4 p-5 rounded-2xl bg-navy text-white hover:bg-[#0a1730] transition-colors"
              >
                <div className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center">
                  <Mail className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest opacity-70">Email Us</div>
                  <div className="font-semibold">{CONTACT.email}</div>
                </div>
              </a>

              <div className="flex items-center gap-4 p-5 rounded-2xl border border-slate-200">
                <div className="w-11 h-11 rounded-xl bg-slate-100 text-navy flex items-center justify-center">
                  <Phone className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-500">Call</div>
                  <div className="font-semibold text-navy">{CONTACT.phone}</div>
                </div>
              </div>

              <div className="flex items-center gap-4 p-5 rounded-2xl border border-slate-200">
                <div className="w-11 h-11 rounded-xl bg-slate-100 text-navy flex items-center justify-center">
                  <MapPin className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-500">Office</div>
                  <div className="font-semibold text-navy">{CONTACT.cityCountry}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-7">
            <form
              onSubmit={submit}
              data-testid="lead-gen-form"
              className="rounded-3xl bg-[#F7F9FC] p-6 lg:p-10 border border-slate-100"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Company Name*" required testid="input-company">
                  <input required value={form.company_name} onChange={set("company_name")} className="input" data-testid="input-company-name" />
                </Field>
                <Field label="Contact Person*" required testid="input-contact">
                  <input required value={form.contact_person} onChange={set("contact_person")} className="input" data-testid="input-contact-person" />
                </Field>
                <Field label="Country*" required>
                  <input required value={form.country} onChange={set("country")} className="input" data-testid="input-country" placeholder="e.g. United Arab Emirates" />
                </Field>
                <Field label="Business Type*" required>
                  <select required value={form.business_type} onChange={set("business_type")} className="input" data-testid="input-business-type">
                    <option value="">Select…</option>
                    {BUSINESS_TYPES.map((b) => (
                      <option key={b} value={b}>{b}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Email*" required>
                  <input type="email" required value={form.email} onChange={set("email")} className="input" data-testid="input-email" placeholder="you@company.com" />
                </Field>
                <Field label="Phone*" required>
                  <input required value={form.phone} onChange={set("phone")} className="input" data-testid="input-phone" placeholder="+1 555 123 4567" />
                </Field>
                <Field label="WhatsApp">
                  <input value={form.whatsapp} onChange={set("whatsapp")} className="input" data-testid="input-whatsapp" placeholder="Optional" />
                </Field>
                <Field label="Estimated Quantity">
                  <input value={form.estimated_quantity} onChange={set("estimated_quantity")} className="input" data-testid="input-quantity" placeholder="e.g. 1 x 20ft container" />
                </Field>
                <Field label="Destination Port">
                  <input value={form.destination_port} onChange={set("destination_port")} className="input" data-testid="input-destination" placeholder="e.g. Jebel Ali, UAE" />
                </Field>
                <div className="sm:col-span-2">
                  <Field label="Products Required*" required>
                    <textarea
                      required
                      rows={3}
                      value={form.products_required}
                      onChange={set("products_required")}
                      className="input resize-y"
                      data-testid="input-products"
                      placeholder="e.g. Aashirvaad 5kg — 500 bags, Sunfeast Marie 200g — 1000 boxes"
                    />
                  </Field>
                </div>
                <div className="sm:col-span-2">
                  <Field label="Message">
                    <textarea
                      rows={3}
                      value={form.message}
                      onChange={set("message")}
                      className="input resize-y"
                      data-testid="input-message"
                      placeholder="Anything else we should know…"
                    />
                  </Field>
                </div>
              </div>

              <div className="mt-7 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:justify-between">
                <p className="text-xs text-slate-500 max-w-md">
                  By submitting this form you agree to be contacted by Babul Corporation
                  regarding your export inquiry.
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="submit-inquiry-button"
                  className="btn-primary disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Submitting…
                    </>
                  ) : (
                    <>
                      Request Quotation <Send className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <style>{`
        .input {
          width: 100%;
          background: #ffffff;
          border: 1px solid #E5E7EB;
          color: #111827;
          padding: 0.85rem 1rem;
          border-radius: 0.75rem;
          font-size: 0.95rem;
          transition: all 200ms;
          font-family: inherit;
        }
        .input:focus {
          outline: none;
          border-color: #005E44;
          box-shadow: 0 0 0 3px rgba(0, 94, 68, 0.12);
        }
      `}</style>
    </section>
  );
}

function Field({ label, children, required }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">
        {label}
      </span>
      {children}
    </label>
  );
}
